import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './MicrositeEmailTemplates.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositeSystemBadge from '../components/MicrositeSystemBadge/MicrositeSystemBadge'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const MicrositeEmailTemplates = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadTemplates = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetEmailTemplates')
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
        } else {
            showToast('Could not load email templates. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadTemplates()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const handleToggleActive = async (row, isActive) => {
        setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive } : r)))
        const res = await post('micrositeUpdateEmailTemplate', { ID: row.ID, ...row, isActive })
        if (res?.status !== 'success') {
            showToast('Could not update template status', 'error')
            setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive: !isActive } : r)))
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteEmailTemplate', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Template deleted')
        } else {
            showToast('Could not delete template', 'error')
        }
    }

    const columns = [
        {
            key: 'templateName',
            label: 'Template',
            render: (row) => (
                <div className={styles.templateCell}>
                    <span className={styles.name}>{row.templateName}</span>
                    {row.isSystem && <MicrositeSystemBadge />}
                </div>
            )
        },
        {
            key: 'subject',
            label: 'Subject line',
            render: (row) => <span className={styles.subject}>{row.subject}</span>
        },
        {
            key: 'isActive',
            label: 'Active',
            render: (row) => (
                <ToggleSwitch size="sm" checked={!!row.isActive} onChange={(v) => handleToggleActive(row, v)} />
            )
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button
                        type="button"
                        className={styles.editLink}
                        onClick={() => navigate(`/microsite/email-templates/${row.ID}/edit`)}
                    >
                        Edit
                    </button>
                    {!row.isSystem && <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />}
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Email templates"
                subtitle="Customize the emails sent to your customers — booking confirmations, reminders, and more."
                primaryAction={{ label: 'New template', icon: <Plus size={14} />, onClick: () => navigate('/microsite/email-templates/new') }}
            />

            <MicrositeTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No email templates yet"
                emptyDescription="System templates will appear here once configured"
            />
        </div>
    )
}

export default MicrositeEmailTemplates